import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { ClientService } from '../client/client.service';

@Injectable({ providedIn: 'root' })
export class AuthClientService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _clientService = inject(ClientService);
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    set typeUser(typeUser: string) {
        localStorage.setItem('typeUser', typeUser);
    }

    get typeUser(): string {
        return localStorage.getItem('typeUser') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the client is already logged in
        if (this._authenticated) {
            return throwError('client is already logged in.');
        }

        return this._httpClient.post(environment.api + '/auth/client/login', credentials).pipe(
            switchMap((response: any) => {
                if (response.status) {
                    // Store the access token in the local storage
                    this.accessToken = response.access_token;
                    this.typeUser = response.type_user;
                    // Set the authenticated flag to true
                    this._authenticated = true;

                    // Store the client on the client service
                    this._clientService.client = response.client;
                }
                // Return a new observable with the response
                return of(response);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Sign in using the token
        return this._httpClient.post(environment.api + '/auth/client/signInWithToken', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) => {
                // Replace the access token with the new one if it's available on
                // the response object.
                if (response.accessToken) {
                    this.accessToken = response.accessToken;
                    this.typeUser = response.type_user;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the client on the client service
                this._clientService.client = response.client;
                if (response.type_user != 'Client') {
                    this._authenticated = true;
                    return of(false);
                }
                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param client
     */
    signUp(client: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', client);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the client is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
